import { CheckCircleIcon, ChevronDownIcon, InformationCircleIcon, MinusCircleIcon } from "@heroicons/react/24/outline";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useHouseholds } from "@/api/hooks/useHouseholds.ts";
import { useIsBuildingOwner } from "@/api/hooks/useIsBuildingOwner";
import Button from "@/components/common/Button";
import { UserInfo } from "@/api/rest/models/account";
import { Building } from "@/api/rest/models/building";
import { HouseholdWithOwnership } from "@/api/rest/models/household";
import { Case, CaseStatus, CaseType } from "@/api/rest/models/warranty";
import InspectionCaseGroup from "@/components/warranty-contents/InspectionCaseGroup";
import { InspectionGroup, groupByInspection, isCompleted } from "@/utils/inspectionGroups";
import { caseTypeNamespaceMap } from "@/utils/translation";

// A list row: a standalone case, or an inspection whose defects render inside it.
type ListEntry = ({ kind: "case"; item: Case } | { kind: "inspection"; group: InspectionGroup }) & {
  activity: string;
  attention: boolean;
};

const caseActivity = (c: Case) => c.lastMessageTimestamp || c.createdAt || "";
const byAttentionThenActivity = (a: ListEntry, b: ListEntry) =>
  Number(b.attention) - Number(a.attention) || b.activity.localeCompare(a.activity);

export default function ListClaims({
  cases,
  showHouseholds = false,
  caseType,
  user,
  building,
}: {
  cases: Case[];
  showHouseholds?: boolean;
  caseType?: CaseType;
  user: UserInfo | null;
  building: Building | undefined;
}) {
  const navigate = useNavigate();
  const households = useHouseholds();
  const householdsbyId = useMemo(() => {
    return households.reduce(
      (acc, h) => {
        acc[h.id] = h;
        return acc;
      },
      {} as Record<number, HouseholdWithOwnership>,
    );
  }, [households]);
  const { t } = useTranslation();
  const namespace = caseType ? caseTypeNamespaceMap.get(caseType) : "case";

  const amIBuildingOwner = useIsBuildingOwner(user, building);

  const [ongoingOpen, setOngoingOpen] = useState(true);
  const [completedOpen, setCompletedOpen] = useState(true);
  const sectionToggleClass =
    "!border-0 !bg-transparent !px-0 !py-0 !shadow-none gap-2 text-sm font-medium text-gray-600 hover:!bg-transparent hover:text-gray-900 focus:!ring-0 focus:!ring-offset-0";

  // Orange means the builder owes the resident a reply. Residents have nothing to act on.
  const needsAttention = useCallback((c: Case) => amIBuildingOwner && Boolean(c.needsBuilderReply), [amIBuildingOwner]);

  const { ongoingEntries, completedEntries } = useMemo(() => {
    const ongoing: ListEntry[] = [];
    const completed: ListEntry[] = [];
    const { standalone, groups } = groupByInspection(cases);

    for (const c of standalone) {
      const entry: ListEntry = { kind: "case", item: c, activity: caseActivity(c), attention: needsAttention(c) };
      (isCompleted(c) ? completed : ongoing).push(entry);
    }

    // An inspection stays ongoing until every defect is closed.
    for (const group of groups) {
      const activity = group.cases.map(caseActivity).reduce((latest, a) => (a > latest ? a : latest), "");
      const attention = group.cases.some(needsAttention);
      (group.cases.every(isCompleted) ? completed : ongoing).push({ kind: "inspection", group, activity, attention });
    }

    ongoing.sort(byAttentionThenActivity);
    completed.sort(byAttentionThenActivity);

    return { ongoingEntries: ongoing, completedEntries: completed };
  }, [cases, needsAttention]);

  const caseTypeRoutes = {
    [CaseType.WARRANTY]: "warranty",
    [CaseType.PROPERTY_MANAGER_CASE]: "property-manager-case",
  };

  // Rows inside an inspection group skip what the group header already shows: apartment and date.
  const renderCaseItem = (c: Case, grouped = false) => {
    const showHousehold = showHouseholds && !grouped;
    const shouldHighlight = needsAttention(c);

    return (
      <div
        key={c.id}
        className={`flex flex-row items-center gap-2 rounded-md py-2 pl-4 pr-2 max-w-[800px] ${
          shouldHighlight ? "bg-orange-200 hover:bg-orange-300" : "bg-white hover:bg-orange-100"
        }`}
      >
        <div
          className="flex flex-col flex-1 cursor-pointer"
          onClick={() => navigate(`/${caseTypeRoutes[c.type]}/${c.id}`)}
        >
          {showHousehold && (
            <span>
              <span className="font-bold">{householdsbyId[c.householdId]?.title || ""}</span> {c.title}
            </span>
          )}
          {!showHousehold && <span className="font-bold">{c.title}</span>}
          <div className="flex flex-row text-gray-500 text-sm items-center">
            {shouldHighlight && <InformationCircleIcon className="h-4 w-4 mr-1 inline text-dobu-orange" />}
            {!shouldHighlight && c.status === CaseStatus.SOLVED && (
              <CheckCircleIcon className="h-4 w-4 mr-1 inline text-green-500" />
            )}
            {!shouldHighlight && c.status === CaseStatus.CLOSED && (
              <MinusCircleIcon className="h-4 w-4 mr-1 inline text-red-600" />
            )}
            <span>
              {t(c.localisedStatus)}
              {shouldHighlight && ` · ${t("case.awaitingReply")}`}
            </span>
            <span className="flex-grow" />
            {!grouped && <span>{new Date(c.createdAt).toLocaleDateString()}</span>}
          </div>
        </div>
      </div>
    );
  };

  const renderEntry = (entry: ListEntry) =>
    entry.kind === "case" ? (
      renderCaseItem(entry.item)
    ) : (
      <InspectionCaseGroup
        key={`inspection-${entry.group.inspection.id}`}
        group={entry.group}
        householdTitle={showHouseholds ? householdsbyId[entry.group.householdId]?.title : undefined}
        attentionCount={entry.group.cases.filter(needsAttention).length}
        // Residents see their one inspection open; builders scan many apartments collapsed.
        defaultOpen={!showHouseholds}
        showExternalNumber={amIBuildingOwner}
        renderCase={(c) => renderCaseItem(c, true)}
      />
    );

  return (
    <>
      <h3 className="text-xl font-semibold">{t(`${namespace}.submittedCases`)}</h3>

      {/* Ongoing Cases Section */}
      {ongoingEntries.length > 0 && (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOngoingOpen(!ongoingOpen)}
            className={sectionToggleClass}
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${ongoingOpen ? "" : "-rotate-90"}`} />
            {t(`${namespace}.ongoingCases`)} ({ongoingEntries.length})
          </Button>
          {ongoingOpen && <div className="flex flex-col gap-2 mt-2">{ongoingEntries.map(renderEntry)}</div>}
        </div>
      )}

      {/* Completed Cases Section */}
      {completedEntries.length > 0 && (
        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setCompletedOpen(!completedOpen)}
            className={sectionToggleClass}
          >
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${completedOpen ? "" : "-rotate-90"}`} />
            {t(`${namespace}.completedCases`)} ({completedEntries.length})
          </Button>
          {completedOpen && <div className="flex flex-col gap-2 mt-2">{completedEntries.map(renderEntry)}</div>}
        </div>
      )}

      {/* Empty state */}
      {ongoingEntries.length === 0 && completedEntries.length === 0 && (
        <p className="text-gray-500 mt-4">{t(`${namespace}.noCases`)}</p>
      )}
    </>
  );
}
